import orderBy from 'lodash/orderBy';
import { getValueOptionByLang } from '@yme/utils/common';
import { ACTION_IMAGE, DEFAULT_LANG, DEGREES_360, GalleryItemType, STATIC_IMAGE, STUDIO_IMAGE, } from '@yme/utils/constants';
import { getFormattedPrice } from '@yme/utils/formatPrice';
import { NA_VALUE } from '../constants';
import { PRODUCT_TYPE_KEY, COLOR_CODE_KEY, COLOR_ID_KEY, COLOR_NAME_KEY, CONTROL_METHOD_KEY, DEFAULT_PRICE_PROPS, E_COMMERCE_ENABLED_KEY, PRICING_DISCLAIMER_KEY, PRODUCT_CAMPAIGN_ID_KEY, PRODUCT_NAME_KEY, ROTATION_KEY, SHAFT_LENGTH_KEY, SIZE_KEY, STARTING_METHOD_KEY, MOTORCYCLE_LOWERED_VERSION_KEY, MOTORCYCLE_TRANSMISSION_KEY, MOTORCYCLE_POWER_VERSION_KEY, DEFAULT_MOTORCYCLES_EXTRA_ATTRS_EN, PRICE_DISCOUNT_HISTORY, ARCHIVE_PRODUCT_ATTR, PRODUCT_OPTIONS_KEY, } from './constants';
// Common helper for getting value in attribute prop by name
export function getAttributeByName(key) {
    return (item) => item?.name.indexOf(key) >= 0;
}
function removalExtension(str) {
    return str.replace(/(?:.jpg|.png)/g, '');
}
function prepareString(inputString, separator) {
    if (inputString) {
        const splittedStr = inputString.split('/');
        const clearedStr = removalExtension(splittedStr[splittedStr.length - 1]);
        return clearedStr.replace(/-|_/g, separator);
    }
    return '';
}
function buildTypeSource(url) {
    const lowerCaseUrl = url.toLowerCase();
    const typeResult = [
        STUDIO_IMAGE,
        STATIC_IMAGE,
        ACTION_IMAGE,
        DEGREES_360,
        GalleryItemType.Detail,
    ].find((item) => lowerCaseUrl.includes(item) && item);
    return typeResult; // TODO: (D.K.) Need investigate negative returned value (for now returned - 'undefined') !!!
}
function buildTransmissionOptionList({ arr = [], prop, power, power35KWLocaleName, is35KW, }) {
    const isConditionMet = is35KW ? power35KWLocaleName === power : power35KWLocaleName !== power;
    if (!isConditionMet) {
        return [...arr];
    }
    if (!arr.length) {
        return [prop];
    }
    if (!prop.length) {
        return [...arr];
    }
    const isPropExist = arr.some((item) => item === prop);
    return !isPropExist ? [...arr, prop] : [...arr];
}
const getFilteredValueByName = (data, key) => data?.find(getAttributeByName(key))?.value;
const MAP_TYPE_TO_ORDER = {
    [GalleryItemType.Degrees360]: 1,
    [GalleryItemType.VIEW360]: 1,
    [GalleryItemType.Action]: 2,
    [GalleryItemType.Static]: 3,
    [GalleryItemType.Studio]: 4,
    [GalleryItemType.Detail]: 5,
};
function getImagesData(data) {
    const { images = [], master } = data;
    const imagesResult = images.reduce((acc = [], item) => {
        if (!item) {
            return [];
        }
        const { url, label: inputLabel, ...rest } = item;
        const type = buildTypeSource(url);
        if (!type) {
            return [...acc];
        }
        const label = prepareString(inputLabel, ' ');
        const order = MAP_TYPE_TO_ORDER[type];
        const preparedImageData = {
            id: url,
            url,
            type,
            label,
            master,
            order,
            ...rest,
        };
        if (!acc.length) {
            return [preparedImageData];
        }
        return [
            ...acc,
            {
                ...preparedImageData,
            },
        ];
    }, []);
    return orderBy(imagesResult, ['order']);
}
function buildOptionList(arr = [], prop) {
    if (!arr.length) {
        return [prop];
    }
    if (!prop.length) {
        return [...arr];
    }
    const isPropExist = arr.some((item) => item === prop);
    return !isPropExist ? [...arr, prop] : [...arr];
}
function buildOptionListWithTransmission({ arr = [], prop, power, power35KWLocaleName, is35KW, }) {
    const condition = is35KW ? power35KWLocaleName === power : power35KWLocaleName !== power;
    if (!condition) {
        return [...arr];
    }
    if (!arr.length) {
        return [prop];
    }
    if (!prop.length) {
        return [...arr];
    }
    const isPropExist = arr.some((item) => item === prop);
    return isPropExist ? [...arr] : [...arr, prop];
}
export function buildFullListImages(data) {
    return data.reduce((acc = [], item) => {
        if (!item?.images) {
            return [];
        }
        const images = getImagesData(item);
        if (!acc) {
            return [...images];
        }
        return [...acc, ...images];
    }, []);
}
// Helpers for getting value from properties by current site-language
export function getAttributesValueByLangOption({ attributes, language, }) {
    // Properties for filter
    const rotation = getFilteredValueByName(attributes, ROTATION_KEY) ?? '';
    const shaftLength = getFilteredValueByName(attributes, SHAFT_LENGTH_KEY) ?? '';
    const startingMethod = getFilteredValueByName(attributes, STARTING_METHOD_KEY) ?? '';
    const controlMethod = getFilteredValueByName(attributes, CONTROL_METHOD_KEY) ?? '';
    const size = getFilteredValueByName(attributes, SIZE_KEY) ?? '';
    const loweredVersion = getFilteredValueByName(attributes, MOTORCYCLE_LOWERED_VERSION_KEY) ?? '';
    const transmission = getFilteredValueByName(attributes, MOTORCYCLE_TRANSMISSION_KEY) ?? '';
    const power = getFilteredValueByName(attributes, MOTORCYCLE_POWER_VERSION_KEY) ?? '';
    // Pricing Info
    const pricingDisclaimer = getFilteredValueByName(attributes, PRICING_DISCLAIMER_KEY) ?? '';
    return {
        // for marine engine (attributes can have multiple languages values)
        rotation: getValueOptionByLang(rotation, language) || NA_VALUE,
        shaftLength: getValueOptionByLang(shaftLength, language) || NA_VALUE,
        startingMethod: getValueOptionByLang(startingMethod, language) || NA_VALUE,
        controlMethod: getValueOptionByLang(controlMethod, language) || NA_VALUE,
        // for eBikes (attributes can have multiple languages values)
        size: getValueOptionByLang(size, language) || NA_VALUE,
        // for motorcycles (attributes can have multiple languages values)
        loweredVersion: getValueOptionByLang(loweredVersion, language) || NA_VALUE,
        transmission: getValueOptionByLang(transmission, language) || NA_VALUE,
        power: getValueOptionByLang(power, language) || NA_VALUE,
        // Pricing Info
        pricingDisclaimer: getValueOptionByLang(pricingDisclaimer, language),
    };
}
// Helpers for getting value from properties without multi language
function getAttributesValue(all) {
    const attributes = all.attributes;
    const colourId = getFilteredValueByName(attributes, COLOR_ID_KEY) ?? '';
    const colourName = getFilteredValueByName(attributes, COLOR_NAME_KEY) ?? '';
    const colourCode = getFilteredValueByName(attributes, COLOR_CODE_KEY) ?? '';
    const eCommerceEnabled = getFilteredValueByName(attributes, E_COMMERCE_ENABLED_KEY) ?? false;
    const productCampaignID = getFilteredValueByName(attributes, PRODUCT_CAMPAIGN_ID_KEY) ?? false;
    const productType = getFilteredValueByName(attributes, PRODUCT_TYPE_KEY) ?? '';
    const productName = getFilteredValueByName(attributes, PRODUCT_NAME_KEY) ?? '';
    const archiveProduct = getFilteredValueByName(attributes, ARCHIVE_PRODUCT_ATTR) ?? '';
    const productOption = getFilteredValueByName(attributes, PRODUCT_OPTIONS_KEY) ?? '';
    const priceDiscountHistory = getFilteredValueByName(attributes, PRICE_DISCOUNT_HISTORY) ?? '';
    return {
        // general and common options
        colourId,
        colourName,
        colourCode,
        productType,
        eCommerceEnabled,
        productCampaignID,
        productName,
        archiveProduct,
        priceDiscountHistory,
        productNameExtended: colourName ? `${productName} ${colourName}` : productName,
        productOption,
    };
}
export function selectProductVariantsByColor({ variantsData, language = DEFAULT_LANG, power35KWLocaleName, }) {
    let isMasterData;
    const variants = variantsData?.reduce((acc = {}, item) => {
        const { sku, stock, prices, master, attributes, productKey, isGroupingProduct } = item;
        const { productName, colourId, colourName, colourCode, productType, archiveProduct, eCommerceEnabled, productCampaignID, productNameExtended, priceDiscountHistory, productOption, } = getAttributesValue({
            attributes,
        });
        const priceDiscountHistoryObj = priceDiscountHistory
            ? JSON.parse(priceDiscountHistory)
            : {
                currency: '',
                history: [],
            };
        const { rotation, loweredVersion, power, transmission, shaftLength, startingMethod, controlMethod, size, pricingDisclaimer, } = getAttributesValueByLangOption({
            attributes,
            language,
        });
        const price = getFormattedPrice({
            attr: prices[0],
            language,
        }) ?? '-';
        if (master) {
            isMasterData = true;
        }
        const currentPrices = prices[0]?.amount
            ? {
                ...DEFAULT_PRICE_PROPS,
                ...(prices[0] || {}),
            }
            : {
                ...DEFAULT_PRICE_PROPS,
            };
        const itemVariant = {
            productName,
            sku,
            stock,
            prices: currentPrices,
            price,
            colourId,
            colourName,
            master,
            rotation,
            productOption,
            loweredVersion,
            power,
            transmission,
            shaftLength,
            startingMethod,
            controlMethod,
            size,
            archiveProduct,
            eCommerceEnabled,
            productCampaignID,
            productType,
            pricingDisclaimer,
            productNameExtended,
            priceDiscountHistory: priceDiscountHistoryObj,
            productKey: productKey ?? '',
            isGroupingProduct: isGroupingProduct ?? false,
            images: getImagesData(item),
        };
        if (Object.keys(acc).length && acc?.[colourName]) {
            const variantsOfProduct = [...acc[colourName].variantsOfProduct, itemVariant].sort((a, b) => a.prices.amount - b.prices.amount);
            return {
                ...acc,
                [colourName]: {
                    ...acc[colourName],
                    variantsOfProduct,
                    skuList: [...(acc[colourName]?.skuList || []), sku],
                    images: getImagesData(item),
                    rotationPossibleOptions: buildOptionList(acc[colourName].rotationPossibleOptions, rotation),
                    shaftLengthPossibleOptions: buildOptionList(acc[colourName].shaftLengthPossibleOptions, shaftLength),
                    startingMethodPossibleOptions: buildOptionList(acc[colourName].startingMethodPossibleOptions, startingMethod),
                    controlMethodPossibleOptions: buildOptionList(acc[colourName].controlMethodPossibleOptions, controlMethod),
                    sizePossibleOptions: buildOptionList(acc[colourName].sizePossibleOptions, size),
                    powerPossibleOptions: buildOptionList(acc[colourName].powerPossibleOptions, power),
                    transmissionPossibleOptions: buildOptionList(acc[colourName].transmissionPossibleOptions, transmission),
                    transmission35KwPossibleOptions: buildTransmissionOptionList({
                        arr: acc[colourName].transmission35KwPossibleOptions,
                        prop: transmission,
                        power,
                        power35KWLocaleName,
                        is35KW: true,
                    }),
                    transmissionFullPowerPossibleOptions: buildTransmissionOptionList({
                        arr: acc[colourName].transmissionFullPowerPossibleOptions,
                        prop: transmission,
                        power,
                        power35KWLocaleName,
                        is35KW: false,
                    }),
                    loweredVersionPossibleOptions: buildOptionList(acc[colourName].loweredVersionPossibleOptions, loweredVersion),
                    productsPossibleOptions: buildOptionList(acc[colourName].productsPossibleOptions, productOption),
                    isMasterIncluded: isMasterData || master,
                    productNameExtended,
                },
            };
        }
        return {
            ...acc,
            [colourName]: {
                colourId,
                colourCode,
                colourName,
                variantsOfProduct: [itemVariant],
                skuList: [sku],
                productKey: productKey ?? '',
                images: getImagesData(item),
                productType,
                isMasterIncluded: isMasterData || master,
                rotationPossibleOptions: buildOptionList([], rotation),
                shaftLengthPossibleOptions: buildOptionList([], shaftLength),
                startingMethodPossibleOptions: buildOptionList([], startingMethod),
                controlMethodPossibleOptions: buildOptionList([], controlMethod),
                sizePossibleOptions: buildOptionList([], size),
                powerPossibleOptions: buildOptionList([], power),
                transmissionPossibleOptions: buildOptionList([], transmission),
                transmission35KwPossibleOptions: buildOptionListWithTransmission({
                    arr: [],
                    prop: transmission,
                    power,
                    power35KWLocaleName,
                    is35KW: true,
                }),
                transmissionFullPowerPossibleOptions: buildOptionListWithTransmission({
                    arr: [],
                    prop: transmission,
                    power,
                    power35KWLocaleName,
                    is35KW: false,
                }),
                loweredVersionPossibleOptions: buildOptionList([], loweredVersion),
                productsPossibleOptions: buildOptionList([], productOption),
                productNameExtended,
            },
        };
    }, {});
    const colorVariantOptions = Object.values(variants);
    // TODO: (D.K.) Need fix typing with any and add necessary types for this function
    const result = colorVariantOptions
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            colorVariantOptions.sort((a, b) => b.isMasterIncluded - a.isMasterIncluded)
        : [];
    return result;
}
export const getFullPowerYAMTVariant = ({ isGroupingProduct, data, }) => {
    if (!isGroupingProduct) {
        return;
    }
    const groupingProducts = data?.groupingProducts;
    let fallbackVariant;
    const variant = groupingProducts?.reduce((acc, product) => {
        return (acc ||
            product.variants.find((variant) => {
                const transmission = variant?.attributes.find(getAttributeByName(MOTORCYCLE_TRANSMISSION_KEY))?.value;
                const power = variant?.attributes.find(getAttributeByName(MOTORCYCLE_POWER_VERSION_KEY))?.value;
                const loweredVersion = variant?.attributes.find(getAttributeByName(MOTORCYCLE_LOWERED_VERSION_KEY))?.value;
                const isMasterVariant = variant.master &&
                    transmission?.en === DEFAULT_MOTORCYCLES_EXTRA_ATTRS_EN.yAMTTransmission &&
                    power?.en === DEFAULT_MOTORCYCLES_EXTRA_ATTRS_EN.fullPower;
                if (isMasterVariant &&
                    !fallbackVariant &&
                    loweredVersion?.en !== DEFAULT_MOTORCYCLES_EXTRA_ATTRS_EN.notLoweredVersion) {
                    fallbackVariant = variant;
                }
                return (isMasterVariant &&
                    loweredVersion?.en === DEFAULT_MOTORCYCLES_EXTRA_ATTRS_EN.notLoweredVersion);
            }));
    }, undefined);
    return variant?.sku ?? fallbackVariant?.sku ?? '';
};
export const hasGroupingProducts = (data) => {
    const groupingProducts = data?.groupingProducts;
    return Boolean(groupingProducts && groupingProducts.length);
};
export const getGroupingDataVariants = (groupingProducts = []) => {
    return (groupingProducts?.reduce((acc, group) => {
        const variantsWithProductKey = group.variants.map((variant) => ({
            ...variant,
            master: false,
            productKey: group.key,
            isGroupingProduct: true,
        }));
        acc.push(...variantsWithProductKey);
        return acc;
    }, []) ?? []);
};
export const getAllVariantsForProduct = (masterProductVariants, groupingVariants) => {
    return [...(masterProductVariants ?? []), ...(groupingVariants ?? [])];
};
